import { useState, useEffect } from "react";
import { toast } from "sonner";
import { ArrowRight, MapPin, Package, X, CheckCircle2, ChevronLeft, ChevronRight, Camera } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { FadeUp, Stagger, StaggerItem } from "@/components/Reveal";
import { api } from "@/lib/api";

const inputCls = "w-full bg-white border border-navy/20 px-4 py-3 text-navy placeholder:text-navy/40 focus:outline-none focus:border-emerald focus:ring-1 focus:ring-emerald transition-colors";
const FALLBACK = "/images/scrap-bales.png";

// Return the photo gallery for a listing, tolerant of legacy single-image records.
const galleryOf = (l) => {
  const imgs = (l.images && l.images.length) ? l.images : (l.image ? [l.image] : []);
  return imgs.length ? imgs : [FALLBACK];
};

const Lightbox = ({ images, startIdx, onClose }) => {
  const [i, setI] = useState(startIdx || 0);
  const prev = (e) => { e.stopPropagation(); setI((i - 1 + images.length) % images.length); };
  const next = (e) => { e.stopPropagation(); setI((i + 1) % images.length); };
  return (
    <div className="fixed inset-0 z-[70] bg-navy/95 flex items-center justify-center p-4" onClick={onClose} data-testid="lightbox">
      <button onClick={onClose} data-testid="close-lightbox" className="absolute top-6 right-6 text-white/70 hover:text-white"><X size={28} /></button>
      <div className="relative max-w-5xl max-h-[85vh] w-full" onClick={(e) => e.stopPropagation()}>
        <img src={images[i]} alt={`photo ${i + 1}`} className="w-full h-[85vh] object-contain" />
        {images.length > 1 && (
          <>
            <button onClick={prev} data-testid="lightbox-prev"
              className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 hover:bg-emerald hover:text-navy text-white flex items-center justify-center"><ChevronLeft size={22} /></button>
            <button onClick={next} data-testid="lightbox-next"
              className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 hover:bg-emerald hover:text-navy text-white flex items-center justify-center"><ChevronRight size={22} /></button>
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-navy/60 text-white font-mono text-xs uppercase tracking-widest px-4 py-2">
              {i + 1} / {images.length}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const BidModal = ({ listing, onClose }) => {
  const empty = { name: "", company: "", email: "", phone: "", price: "", quantity: "", message: "" };
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.price) { toast.error("Name, email and your offer price are required."); return; }
    setLoading(true);
    try {
      await api.post(`/listings/${listing.id}/bids`, form);
      toast.success("Offer submitted! We'll review and get back to you.");
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed. Try again.");
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-navy/80 flex items-center justify-center p-4 md:p-6" onClick={onClose} data-testid="bid-modal">
      <div className="bg-white max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between px-6 py-4 border-b border-navy/10 sticky top-0 bg-white">
          <div>
            <div className="font-mono text-xs uppercase tracking-widest text-emerald">Make an Offer</div>
            <div className="font-display font-black text-xl text-navy tracking-tight mt-1">{listing.title}</div>
          </div>
          <button onClick={onClose} data-testid="close-bid" className="text-navy/40 hover:text-navy"><X size={22} /></button>
        </div>
        {done ? (
          <div className="p-10 text-center" data-testid="bid-success">
            <CheckCircle2 className="text-emerald mx-auto mb-6" size={48} />
            <h3 className="font-display font-black text-2xl text-navy tracking-tight">Offer received</h3>
            <p className="text-navy/60 mt-3">Thank you — our team will contact you shortly.</p>
            <button onClick={onClose} className="mt-8 bg-navy text-white font-mono text-xs uppercase tracking-widest px-6 py-3 hover:bg-emerald hover:text-navy transition-colors">Close</button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-6 space-y-4" data-testid="bid-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <input className={inputCls} placeholder="Your name *" value={form.name} onChange={set("name")} data-testid="bid-name" />
              <input className={inputCls} placeholder="Company" value={form.company} onChange={set("company")} data-testid="bid-company" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <input className={inputCls} type="email" placeholder="Email *" value={form.email} onChange={set("email")} data-testid="bid-email" />
              <input className={inputCls} placeholder="Phone" value={form.phone} onChange={set("phone")} data-testid="bid-phone" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <input className={inputCls} placeholder="Your offer price * (e.g. $0.55/lb)" value={form.price} onChange={set("price")} data-testid="bid-price" />
              <input className={inputCls} placeholder="Quantity wanted" value={form.quantity} onChange={set("quantity")} data-testid="bid-quantity" />
            </div>
            <textarea className={`${inputCls} min-h-[110px] resize-none`} placeholder="Message (pickup, terms, questions...)" value={form.message} onChange={set("message")} data-testid="bid-message" />
            <button type="submit" disabled={loading} data-testid="bid-submit"
              className="w-full bg-emerald text-navy font-mono text-xs uppercase tracking-widest px-6 py-4 hover:bg-navy hover:text-white transition-colors disabled:opacity-50">
              {loading ? "Submitting..." : "Submit Offer"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

const Offers = () => {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [lightbox, setLightbox] = useState(null); // { images, startIdx }

  useEffect(() => {
    api.get("/listings").then((r) => setListings(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="offers-page">
      <PageHero eyebrow="/ MATERIALS FOR SALE" title={["Available", "lots."]}
        subtitle="Recent lots offered through SAYE IMPEX. Recently completed deals shown as SOLD. Available lots — make an offer." />

      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28">
        {loading ? (
          <p className="font-mono text-sm text-navy/50">Loading listings...</p>
        ) : listings.length === 0 ? (
          <FadeUp>
            <div className="border border-navy/10 p-16 text-center">
              <Package className="text-emerald mx-auto mb-6" size={40} strokeWidth={1.5} />
              <h3 className="font-display font-black text-2xl text-navy tracking-tight">No lots listed right now</h3>
              <p className="text-navy/60 mt-3 max-w-md mx-auto">Check back soon, or reach out and tell us what material you're looking for.</p>
            </div>
          </FadeUp>
        ) : (
          <Stagger className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {listings.map((l) => {
              const isSold = l.status === "sold";
              const photos = galleryOf(l);
              const main = photos[0];
              return (
                <StaggerItem key={l.id}>
                  <div className={`group border border-navy/10 flex flex-col h-full transition-colors ${isSold ? "opacity-95" : "hover:border-emerald"}`} data-testid={`listing-${l.id}`}>
                    <button type="button" onClick={() => setLightbox({ images: photos, startIdx: 0 })}
                      data-testid={`open-photos-${l.id}`} className="relative overflow-hidden text-left">
                      <img src={main} alt={l.title} onError={(e) => { e.currentTarget.src = FALLBACK; }}
                        className={`w-full aspect-[4/3] object-cover transition-transform duration-500 ${isSold ? "grayscale-[0.35]" : "group-hover:scale-105"}`} />
                      <span className="absolute top-4 left-4 bg-emerald text-navy font-mono text-xs px-3 py-1 tracking-widest">{l.material_type}</span>
                      {photos.length > 1 && (
                        <span className="absolute bottom-4 left-4 bg-navy/80 text-white font-mono text-xs px-3 py-1 tracking-widest flex items-center gap-2"
                          data-testid={`photo-count-${l.id}`}>
                          <Camera size={12} /> {photos.length} photos
                        </span>
                      )}
                      {isSold && (
                        <span data-testid={`sold-badge-${l.id}`}
                          className="absolute top-4 right-4 bg-navy text-white font-display font-black text-sm px-4 py-1.5 tracking-widest border-2 border-white shadow-lg">
                          SOLD
                        </span>
                      )}
                    </button>
                    <div className="p-6 flex flex-col flex-1">
                      <h3 className="font-display font-black text-xl md:text-2xl text-navy tracking-tight leading-tight">{l.title}</h3>
                      <div className="mt-4 space-y-1.5 font-mono text-xs text-navy/60">
                        {l.quantity && <div className="flex items-center gap-2"><Package size={14} className="text-emerald" /> {l.quantity}</div>}
                        {l.location && <div className="flex items-center gap-2"><MapPin size={14} className="text-emerald" /> {l.location}</div>}
                      </div>
                      {isSold ? (
                        <div className="mt-6 w-full bg-navy text-white font-display font-black text-sm uppercase tracking-[0.3em] px-5 py-3 flex items-center justify-center"
                          data-testid={`sold-note-${l.id}`}>
                          SOLD
                        </div>
                      ) : (
                        <button onClick={() => setActive(l)} data-testid={`make-offer-${l.id}`}
                          className="mt-6 w-full bg-navy text-white font-mono text-xs uppercase tracking-widest px-5 py-3 flex items-center justify-center gap-2 hover:bg-emerald hover:text-navy transition-colors">
                          Make an Offer <ArrowRight size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                </StaggerItem>
              );
            })}
          </Stagger>
        )}
      </section>

      {active && <BidModal listing={active} onClose={() => setActive(null)} />}
      {lightbox && <Lightbox images={lightbox.images} startIdx={lightbox.startIdx} onClose={() => setLightbox(null)} />}
    </div>
  );
};

export default Offers;
